import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";

const NotificationHistory = () => {
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: "",
    audience: "",
    category: "",
    type: "",
    fromDate: "",
    toDate: "",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState("");
  const roleDropdownRef = useRef(null);
  const audienceDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const limit = 30;

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleOpen(false);
      }
      if (audienceDropdownRef.current && !audienceDropdownRef.current.contains(event.target)) {
        setIsAudienceOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setIsTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const res = await axios.get("https://visitguru.in/api/v1/category/getCategory", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setCategories(res.data.data || []);
      } else {
        toast.error(res.data.message || "No categories found");
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories: " + err.message);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const res = await axios.get("https://visitguru.in/api/v1/notification/history", {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 10000 },
      });

      if (res.data.success) {
        const data = res.data.data || [];
        setAllNotifications(data);
        applyFilters(data);
      } else {
        toast.error(res.data.message || "No notifications found");
        setAllNotifications([]);
        setNotifications([]);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      toast.error("Failed to load notifications: " + err.message);
      setAllNotifications([]);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete all notifications (frontend + backend)
  const handleDeleteAll = async () => {
    if (!window.confirm("Are you sure you want to delete all notifications?")) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const res = await axios.delete("https://visitguru.in/api/v1/notification/delete-all", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        toast.success("All notifications deleted successfully");
        setAllNotifications([]);
        setNotifications([]);
      } else {
        toast.error(res.data.message || "Failed to delete notifications");
      }
    } catch (err) {
      console.error("Error deleting notifications:", err);
      toast.error("Error deleting notifications: " + err.message);
    }
  };

  // Apply filters
  const applyFilters = (data) => {
    let filtered = [...data];
    if (filters.role) filtered = filtered.filter((n) => n.role === filters.role);
    if (filters.audience) filtered = filtered.filter((n) => n.audience === filters.audience);
    if (filters.type) filtered = filtered.filter((n) => n.type === filters.type);
    if (filters.category) {
      const selectedCategoryName = categories.find((c) => c._id === filters.category)?.categoryName;
      filtered = filtered.filter(
        (n) =>
          Array.isArray(n.categories) &&
          (selectedCategoryName ? n.categories.includes(selectedCategoryName) : true)
      );
    }
    if (filters.fromDate) {
      filtered = filtered.filter((n) => new Date(n.sentAt) >= new Date(filters.fromDate));
    }
    if (filters.toDate) {
      filtered = filtered.filter((n) => new Date(n.sentAt) <= new Date(filters.toDate));
    }

    setNotifications(filtered);
    setTotalPages(Math.ceil(filtered.length / limit));
    setPage(1);
  };

  useEffect(() => {
    fetchCategories();
    fetchNotifications();
  }, []);

  useEffect(() => {
    applyFilters(allNotifications);
  }, [filters, allNotifications]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      role: "",
      audience: "",
      category: "",
      type: "",
      fromDate: "",
      toDate: "",
    });
    setSearchCategory("");
  };

  // Download PDF
  const handleDownload = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Notification History", 14, 22);

    const columns = [
      "#",
      "Title",
      "Body",
      "Type",
      "Role",
      "Audience",
      "Categories",
      "Video",
      "Sent At",
    ];
    const rows = notifications.map((n, index) => [
      (page - 1) * limit + index + 1,
      n.title || "N/A",
      n.body || "N/A",
      n.type || "N/A",
      n.role || "N/A",
      n.audience || "N/A",
      Array.isArray(n.categories) && n.categories.length > 0 ? n.categories.join(", ") : "N/A",
      n.videoUrl ? "Video/Link" : "N/A",
      new Date(n.sentAt).toLocaleString(),
    ]);

    doc.autoTable({
      startY: 30,
      head: [columns],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
      theme: "striped",
    });

    doc.save("notification-history.pdf");
  };

  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "vendor", label: "Vendor" },
    { value: "employee", label: "Employee" },
    { value: "user", label: "User" },
  ];

  const audienceOptions = [
    { value: "", label: "All Audience" },
    { value: "all", label: "All Users" },
    { value: "subscribed", label: "Subscribed" },
    { value: "nonsubscribed", label: "Non Subscribed" },
  ];

  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...categories.map((cat) => ({
      value: cat._id,
      label: cat.categoryName || "N/A",
    })),
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "Text", label: "Text" },
    { value: "Video", label: "Video" },
  ];

  const filteredCategoryOptions = categoryOptions.filter((cat) =>
    cat.label.toLowerCase().includes(searchCategory.toLowerCase())
  );

  const currentData = notifications.slice((page - 1) * limit, page * limit);

  return (
    <div className="relative flex flex-col bg-white w-full mb-6 shadow-lg rounded-lg">
      <div className="px-4 py-3 border-b border-blueGray-200">
        <h3 className="font-semibold text-base text-blueGray-700 mb-2">
          Notification History{" "}
          <span className="ml-2 text-sm text-blueGray-500">(Total: {notifications.length})</span>
          <div style={{ float: "right", display: "flex", gap: "10px" }}>
            <button
              onClick={handleDownload}
              style={{
                backgroundColor: "#0884c4",
                width: "100px",
                height: "40px",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Download
            </button>
            <button
              onClick={handleDeleteAll}
              style={{
                backgroundColor: "#e53935",
                width: "120px",
                height: "40px",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Delete All
            </button>
          </div>
        </h3>

        {/* FILTERS */}
        <div className="bg-gray-50 border rounded-md p-4 mx-4 mb-4 shadow-sm">
          <div className="flex flex-wrap gap-4 mb-4">
            {/* Role Dropdown */}
            <div className="relative w-1/5" ref={roleDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <div
                className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
                onClick={() => {
                  setIsRoleOpen((prev) => !prev);
                  setIsAudienceOpen(false);
                  setIsCategoryOpen(false);
                  setIsTypeOpen(false);
                }}
              >
                {roleOptions.find((opt) => opt.value === filters.role)?.label ||
                  "Select Role"}
              </div>
              {isRoleOpen && (
                <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                  {roleOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        handleFilterChange("role", opt.value);
                        setIsRoleOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Audience Dropdown */}
            <div className="relative w-1/5" ref={audienceDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Audience
              </label>
              <div
                className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
                onClick={() => {
                  setIsAudienceOpen((prev) => !prev);
                  setIsRoleOpen(false);
                  setIsCategoryOpen(false);
                  setIsTypeOpen(false);
                }}
              >
                {audienceOptions.find((opt) => opt.value === filters.audience)
                  ?.label || "Select Audience"}
              </div>
              {isAudienceOpen && (
                <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                  {audienceOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        handleFilterChange("audience", opt.value);
                        setIsAudienceOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            <div className="relative w-1/5" ref={categoryDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <div
                className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
                onClick={() => {
                  setIsCategoryOpen((prev) => !prev);
                  setIsRoleOpen(false);
                  setIsAudienceOpen(false);
                  setIsTypeOpen(false);
                }}
              >
                {categoryOptions.find((opt) => opt.value === filters.category)
                  ?.label || "Select Category"}
              </div>
              {isCategoryOpen && (
                <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                  <input
                    type="text"
                    placeholder="Search category..."
                    value={searchCategory}
                    onChange={(e) => setSearchCategory(e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 focus:outline-none text-sm"
                  />
                  {filteredCategoryOptions.length > 0 ? (
                    filteredCategoryOptions.map((cat) => (
                      <div
                        key={cat.value}
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                        onClick={() => {
                          handleFilterChange("category", cat.value);
                          setIsCategoryOpen(false);
                          setSearchCategory("");
                        }}
                      >
                        {cat.label}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      No categories found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Type Dropdown */}
            <div className="relative w-1/5" ref={typeDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <div
                className="w-full p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
                onClick={() => {
                  setIsTypeOpen((prev) => !prev);
                  setIsRoleOpen(false);
                  setIsAudienceOpen(false);
                  setIsCategoryOpen(false);
                }}
              >
                {typeOptions.find((opt) => opt.value === filters.type)?.label ||
                  "Select Type"}
              </div>
              {isTypeOpen && (
                <div className="absolute z-10 bg-white border border-gray-300 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                  {typeOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        handleFilterChange("type", opt.value);
                        setIsTypeOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From
              </label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                className="w-40 border rounded-md px-2 py-1 text-sm"
                style={{ height: "38px" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To
              </label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
                className="w-40 border rounded-md px-2 py-1 text-sm"
                style={{ height: "38px" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-blueGray-500">
            Loading notifications...
          </div>
        ) : (
          <table className="w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {[
                  "#",
                  "Title",
                  "Body",
                  "Type",
                  "Role",
                  "Audience",
                  "Categories",
                  "Video",
                  "Sent At",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-6 py-3 bg-blueGray-50 text-blueGray-500 text-xs uppercase font-semibold text-left border border-blueGray-100"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((n, index) => (
                  <tr key={n.id || index}>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.title || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.body || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.type || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.role || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.audience || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {Array.isArray(n.categories) && n.categories.length > 0
                        ? n.categories.join(", ")
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {n.videoUrl ? (
                        <>
                          {/* Check if it's an actual video file link */}
                          {/\.(mp4|mov|webm|ogg)$/i.test(n.videoUrl) ? (
                            <div className="flex flex-col items-start">
                              <video
                                src={n.videoUrl}
                                controls
                                className="max-w-xs h-auto rounded"
                                style={{ maxHeight: "100px" }}
                                onError={(e) => {
                                  // Hide video if it fails to load
                                  e.target.style.display = "none";
                                  // Show the "Video Link" fallback
                                  e.target.nextSibling.style.display = "block";
                                }}
                              />
                              <a
                                href={n.videoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-500 hover:underline mt-2"
                                style={{ display: "none" }}
                              >
                                Video Link
                              </a>
                              <a
                                href={n.videoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-500 hover:underline mt-2"
                              >
                                View Full Video
                              </a>
                            </div>
                          ) : (
                            // If it's not a video file link, show as a clickable link
                            <a
                              href={n.videoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              Open Link
                            </a>
                          )}
                        </>
                      ) : (
                        "N/A"
                      )}
                    </td>

                    <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                      {new Date(n.sentAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="p-4 text-center text-sm text-blueGray-500"
                  >
                    No Notification History Found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && notifications.length > limit && (
        <div className="flex justify-between items-center px-4 py-3">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-blueGray-200 rounded hover:bg-blueGray-300 transition disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-blueGray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-blueGray-200 rounded hover:bg-blueGray-300 transition disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;
